import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import Navigation from './Navigation';
import Card from './Card';
import LoadingSpinner from './LoadingSpinner';

function fullDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function ratingColor(rating) {
  switch ((rating || '').toUpperCase()) {
    case 'CRITICAL': return 'bg-red-100 text-red-700 border-red-200';
    case 'HIGH': return 'bg-orange-100 text-orange-700 border-orange-200';
    case 'MEDIUM': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    case 'LOW': return 'bg-green-100 text-green-700 border-green-200';
    default: return 'bg-gray-100 text-gray-600 border-gray-200';
  }
}

function DeltaBadge({ delta }) {
  if (delta === 0) {
    return <span className="flex items-center gap-1 text-gray-400 text-xs"><Minus className="w-3 h-3" /> 0</span>;
  }
  const up = delta > 0;
  return (
    <span className={`flex items-center gap-1 text-xs font-semibold ${up ? 'text-green-600' : 'text-red-600'}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {up ? '+' : ''}{delta}
    </span>
  );
}

function RiskRow({ risk, badge }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 px-3 border-b border-gray-100 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm text-gray-800 truncate" title={risk.title}>{risk.title || 'Untitled risk'}</div>
        {badge && <div className="text-[11px] text-gray-400 mt-0.5">{badge}</div>}
      </div>
      {risk.currentRating || risk.previousRating ? (
        <div className="flex items-center gap-1.5 shrink-0 text-xs">
          {risk.previousRating && (
            <span className={`px-1.5 py-0.5 rounded border ${ratingColor(risk.previousRating)}`}>{risk.previousRating}</span>
          )}
          {risk.previousRating && risk.currentRating && <span className="text-gray-400">→</span>}
          {risk.currentRating && (
            <span className={`px-1.5 py-0.5 rounded border ${ratingColor(risk.currentRating)}`}>{risk.currentRating}</span>
          )}
        </div>
      ) : (
        risk.residualRiskRating && (
          <span className={`px-1.5 py-0.5 rounded border text-xs shrink-0 ${ratingColor(risk.residualRiskRating)}`}>
            {risk.residualRiskRating}
          </span>
        )
      )}
    </div>
  );
}

function RiskSection({ title, risks, emptyText, badgeFor, warn, subtitle }) {
  const heading = warn ? (
    <div className="flex items-center gap-1.5 text-sm font-semibold text-amber-800 mb-2">
      <AlertCircle className="w-4 h-4 shrink-0" />
      {title} <span className="text-amber-500 font-normal">({risks?.length || 0})</span>
    </div>
  ) : (
    <div className="text-sm font-semibold text-gray-800 mb-2">
      {title} {risks?.length > 0 && <span className="text-gray-400 font-normal">({risks.length})</span>}
    </div>
  );

  if (!risks?.length) {
    return (
      <Card>
        {heading}
        <p className="text-xs text-gray-400">{emptyText}</p>
      </Card>
    );
  }
  return (
    <Card>
      {heading}
      {warn && (
        <p className="text-xs text-amber-700 mb-2">
          The questionnaire answer changed, but no remediation for these risks was ever marked completed — treat as unverified.
        </p>
      )}
      {subtitle && <p className="text-[11px] text-amber-600 mb-2">{subtitle}</p>}
      <div className="max-h-80 overflow-y-auto -mx-3">
        {risks.map(r => <RiskRow key={r.unifiedRiskId} risk={r} badge={badgeFor?.(r)} />)}
      </div>
    </Card>
  );
}

export default function EnterpriseAssessmentComparisonView() {
  const { id, previousId } = useParams();
  const navigate = useNavigate();
  const apiCall = useApi();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const fetchComparison = useCallback(async () => {
    setLoaded(false);
    setError(null);
    try {
      const result = await apiCall('GET', `/enterpriseAssessments/${id}/compare/${previousId}`);
      setData(result);
    } catch (err) {
      // The backend 409s with a clear message when either side's analysis isn't complete yet, and
      // 400s when previousId isn't actually in this assessment's history — surface either as a
      // plain message rather than a blank/crashed page or a silent retry loop.
      const status = err?.response?.status;
      const message = err?.response?.data?.message;
      if (status === 409) {
        setError(message || 'This comparison is not ready yet — analysis is still in progress.');
      } else if (status === 400) {
        setError(message || 'These two assessments are not part of the same history.');
      } else {
        setError('Could not load this comparison.');
      }
    } finally {
      setLoaded(true);
    }
  }, [apiCall, id, previousId]);

  useEffect(() => { fetchComparison(); }, [fetchComparison]);

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="max-w-6xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <h1 className="text-2xl font-bold text-content-primary mb-1">Assessment Comparison</h1>

        {!loaded ? (
          <LoadingSpinner />
        ) : error ? (
          <Card>
            <div className="flex items-center gap-2 text-amber-700">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          </Card>
        ) : !data ? null : (
          <>
            <p className="text-content-secondary mb-4 text-sm">
              {fullDate(data.previousDate)} <span className="text-gray-400">vs</span> {fullDate(data.currentDate)}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <Card>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">New</div>
                <div className="text-2xl font-bold text-red-600">{data.newRiskCount}</div>
              </Card>
              <Card>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Resolved</div>
                <div className="text-2xl font-bold text-green-600">{data.resolvedRiskCount}</div>
                {data.resolvedUnverifiedCount > 0 && (
                  <div className="text-[11px] text-amber-600 mt-0.5">{data.resolvedUnverifiedCount} self-attested, unverified</div>
                )}
              </Card>
              <Card>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Persisted</div>
                <div className="text-2xl font-bold text-gray-700">{data.persistedRiskCount}</div>
                {data.changedRiskCount > 0 && (
                  <div className="text-[11px] text-amber-600 mt-0.5">{data.changedRiskCount} with a changed rating</div>
                )}
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {data.frameworkDeltas?.length > 0 && (
                <Card>
                  <div className="text-sm font-semibold text-gray-800 mb-2">Framework Score Deltas</div>
                  <div className="divide-y divide-gray-100">
                    {data.frameworkDeltas.map(fw => (
                      <div key={fw.framework} className="flex items-center justify-between py-1.5 text-sm">
                        <span className="text-gray-700">{fw.framework}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-gray-400 text-xs">{fw.previousScore} → {fw.currentScore}</span>
                          <DeltaBadge delta={fw.delta} />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
              <RiskSection
                title="New Risks"
                risks={data.newRisks}
                emptyText="No new risks since the previous assessment."
                badgeFor={r => r.newDueToQuestionChurn ? 'New question — not in the previous assessment' : null}
              />
            </div>

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <RiskSection
                title="Resolved — Verified Remediation"
                risks={data.resolvedRisks?.filter(r => r.verifiedRemediation)}
                emptyText="No verified resolutions since the previous assessment."
                badgeFor={r => r.resolvedDueToQuestionRetirement ? 'Question no longer asked' : null}
              />
              <RiskSection
                title="Resolved — Self-Attested (No Verified Remediation)"
                risks={data.resolvedRisks?.filter(r => !r.verifiedRemediation)}
                emptyText="No self-attested (unverified) resolutions since the previous assessment."
                badgeFor={r => r.resolvedDueToQuestionRetirement ? 'Question no longer asked' : null}
                warn
              />
            </div>

            <div className="mt-4">
              <RiskSection
                title="Persisted Risks"
                risks={data.persistedRisks}
                emptyText="No risks persisted between these two assessments."
                subtitle={data.changedRiskCount > 0 ? `${data.changedRiskCount} with a changed rating` : null}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
